import React, { useMemo, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { FiFilter } from 'react-icons/fi';
import { Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  clearModalSideFilter,
  clearSideFilter,
  setModalSideFilters,
} from '../../../modules/SearchDatePicker/searchDatePickerSlice';
import { OptionalFilter } from '../interface';

interface Props {
  optional_filters: OptionalFilter[];
  inactiveMode?: 'disabled' | 'hidden';
  handleOnClear: () => void;
  withModalScopeParams?: boolean;
  filterEntityType?: 'dynamic_flights' | 'bundle_flights';
  isReturnFlight?: boolean;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
}

const ClearFiltersButton: React.FC<Props> = ({
  optional_filters,
  inactiveMode = 'disabled',
  handleOnClear,
  withModalScopeParams = false,
  filterEntityType,
  isReturnFlight,
  localSideFilters,
  setLocalSideFilters
}) => {
  const dispatch = useDispatch();
  const { sideFilters: sideFilters_t } = useSelector((state: RootState) => {
    return state.config.translations;
  });
  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => {
      return state.search;
    }
  );
  const [sideFilters, setSideFilters] = useState({});
  // const sideFilters = useMemo(
  //   () => (withModalScopeParams ? modalSideFilters : reduxSideFilters),
  //   [withModalScopeParams, modalSideFilters, reduxSideFilters]
  // );

  function removeParamsFromURL() {
    var url = new URL(window.location.href);
    const allOptFilterKeys =
      optional_filters?.map((filter) => filter.attr) || [];
    // manually add sortBy param since it's not in the optional_filters obj
    if (allOptFilterKeys.length > 0) {
      allOptFilterKeys.push('sortBy');
    }
    var filterKeys = false ? allOptFilterKeys : Object.keys(sideFilters);

    if (filterEntityType === 'dynamic_flights') {
      if (isReturnFlight) {
        filterKeys = filterKeys.filter((key) => key.startsWith('f2_'));
      } else {
        filterKeys = filterKeys.filter((key) => key.startsWith('f1_'));
      }
    }

    filterKeys.forEach((key) => {
      url.searchParams.delete(key);
    });
    window.history.pushState({}, '', url.toString());
    dispatch(clearSideFilter({ filterEntityType, isReturnFlight }));
  }

  function handleClearModalFilters() {
    dispatch(clearSideFilter({ filterEntityType, isReturnFlight }));
    dispatch(clearModalSideFilter({ filterEntityType, isReturnFlight }));
  }

  const handleClear = () => {
    if (localSideFilters && setLocalSideFilters) {
      setLocalSideFilters({});
      return;
    }

    if (withModalScopeParams) {
      handleClearModalFilters();
    } else {
      removeParamsFromURL();
    }
    handleOnClear && handleOnClear();
  };


  useEffect(() => {
    if(localSideFilters){
      setSideFilters(localSideFilters);
      return;
    }
    const allSideFilters = withModalScopeParams
      ? modalSideFilters
      : reduxSideFilters;

    if (!filterEntityType || filterEntityType !== 'dynamic_flights') {
      setSideFilters(allSideFilters);
      return;
    }

    let dynamicFlightFilters: Record<string, any> = {};

    for (const key in allSideFilters) {
      if (key.startsWith(isReturnFlight ? 'f2_' : 'f1_')) {
        dynamicFlightFilters[key] = allSideFilters[key];
      }
    }
    setSideFilters(dynamicFlightFilters);
  }, [
    filterEntityType,
    isReturnFlight,
    withModalScopeParams,
    modalSideFilters,
    reduxSideFilters,
    localSideFilters
  ]);

  // We consider the whole set of side filters "empty" only when all values are:
  // - nullish (`null`/`undefined`)
  // - empty arrays (`[]`)
  // - empty/whitespace-only strings (`''` / `'   '`)
  // Numbers are always treated as non-empty (including `0`).
  const isNilOrBlankStringOrEmptyArray = (value: any) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (typeof value === 'object') return isEmpty(value);
    if (Array.isArray(value)) return value.length === 0;
    return false;
  };

  const isSideFiltersEmpty = useMemo(() => {
    if (isEmpty(sideFilters)) {
      return true;
    }
    const keys = Object.keys(sideFilters ?? {});
    return keys.every((key) =>
      isNilOrBlankStringOrEmptyArray((sideFilters as any)?.[key])
    );
  }, [sideFilters]);

  const ClearButton = () => {
    return (
      <Button
        className={inactiveMode === 'hidden' ? 'btn-style-5' : 'btn-style-4'}
        disabled={inactiveMode !== 'hidden' && isSideFiltersEmpty}
        onClick={handleClear}
      >
        <FiFilter /> {sideFilters_t.clearSideFilters}
      </Button>
    );
  };

  if (inactiveMode === 'hidden') {
    return (
      <div
        className={'clear-side-filters'}
        style={{
          display: !isSideFiltersEmpty ? 'block' : 'none',
        }}
      >
        <ClearButton />
      </div>
    );
  }

  return <ClearButton />;
};

export default ClearFiltersButton;
